<!-- CertCardWorkOrderPrintModal — 수기 작업지시서 제출 직후 인쇄 유도 모달 (SPEC-CHINA-CERT-CARD-PRINT-001) -->
<!-- CertificateWorkOrder/index.vue complete() 성공 콜백에서, 이번 제출에 실물카드(randomNo)가
     연결된 경우에만 연다. cards prop은 이미 제출된 workOrderData에서 뽑은 값이라 BE 재조회가
     필요 없다 — assignedWorkOrderId는 이 시점엔 알 수 없으므로 인쇄 버튼은 항상 활성(실인쇄
     가능 여부는 CertCardPrintModal 자체의 print-data linked 판정이 최종 게이트다). -->
<!-- open은 부모가 전적으로 소유한다(v-model 아님) — 이 컴포넌트는 update:open을 emit하지 않고,
     대신 사용자의 의도를 구분해 두 이벤트만 올린다: continue(계속 만들기, 위저드에 남음),
     gotoList(목록으로 가기, 목록 이동). X/마스크/Esc로 닫는 것도 전부 continue와 동일하게 취급한다
     (사용자가 명시적으로 '목록으로 가기'를 누르지 않은 이상 절대 다른 화면으로 이동시키지 않는다). -->
<!-- 사용법: <cert-card-work-order-print-modal :open="printPromptModal.open" :cards="printPromptModal.cards"
     @continue="onPrintPromptContinue" @goto-list="onPrintPromptGotoList" /> -->
<template>
  <a-modal
    :open="open"
    :title="$t('certCard_print_wo_modal_title')"
    width="640px"
    :closable="true"
    destroy-on-close
    wrap-class-name="cert-card-wo-print-overlay"
    @cancel="$emit('continue')"
  >
    <p style="margin-bottom: 12px; font-size: 13px; color: #555;">
      {{ $t('certCard_print_wo_explainer') }}
    </p>
    <!-- 인쇄 미리보기 자체가 아니라 온스크린 모달 셸의 목록 — x-스크롤만 추가, 인쇄 파이프라인
         (cert-card-print-modal, @media print)은 이 파일 밖이라 손대지 않는다. -->
    <a-table
      :columns="columns"
      :data-source="items"
      :pagination="false"
      size="small"
      :row-key="(r) => r.randomNo"
      :scroll="{ x: 620, y: 360 }"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'randomNo'">
          <span style="font-family: monospace; font-size: 12px;">No.{{ record.randomNo }}</span>
        </template>
        <template v-else-if="column.key === 'gtin'">
          <span style="font-family: monospace; font-size: 12px;">{{ record.gtin || '-' }}</span>
        </template>
        <template v-else-if="column.key === 'teeth'">
          <span style="font-size: 12px;">{{ (record.teeth && record.teeth.length) ? record.teeth.join(', ') : '-' }}</span>
        </template>
        <template v-else-if="column.key === 'printedAt'">
          <span style="font-size: 12px;">{{ formatDate(record.printedAt) }}</span>
        </template>
        <template v-else-if="column.key === 'printAction'">
          <a-button size="small" @click="openPrintModal(record)">{{ $t('certCard_print_row_btn') }}</a-button>
        </template>
      </template>
    </a-table>

    <!-- 실물카드 단건 인쇄 모달 -->
    <cert-card-print-modal
      v-model:open="printModal.open"
      :card="printModal.card"
      @printed="onCardPrinted"
    />

    <template #footer>
      <!-- 두 버튼 — "계속 만들기"는 위저드에 남아 다음 작업지시서를 바로 작성, "목록으로 가기"만
           실제 이동을 트리거한다. 사용자가 명시적으로 이동을 선택하지 않는 한 화면을 벗어나지 않는다. -->
      <a-button @click="$emit('continue')">
        {{ $t('certCard_print_wo_continue_btn') }}
      </a-button>
      <a-button type="primary" @click="$emit('gotoList')">
        {{ $t('certCard_print_wo_goto_list_btn') }}
      </a-button>
    </template>
  </a-modal>
</template>

<script>
// @MX:NOTE: [AUTO] CertCardWorkOrderPrintModal — 수기 작업지시서 제출 성공 직후, 이번 제출에 연결된
// 실물카드가 있으면 인쇄를 유도하는 모달. cards prop은 BE 재조회 없이 제출 payload에서 그대로 온다.
import CertCardPrintModal from './CertCardPrintModal.vue';

export default {
  name: 'CertCardWorkOrderPrintModal',

  components: { CertCardPrintModal },

  props: {
    open: { type: Boolean, default: false },
    // [{ randomNo, gtin, teeth: number[] }]
    cards: { type: Array, default: () => [] },
  },

  emits: ['continue', 'gotoList'],

  data() {
    return {
      // cards prop의 로컬 편집 가능 사본 — printed 이벤트로 printedAt만 패치한다(prop 직접 변형 금지).
      items: [],
      printModal: {
        open: false,
        card: null,
      },
    };
  },

  computed: {
    columns() {
      return [
        { title: this.$t('certCard_verify_col_randomNo'), dataIndex: 'randomNo', key: 'randomNo', width: 140 },
        { title: 'GTIN', dataIndex: 'gtin', key: 'gtin' },
        { title: this.$t('certCard_print_wo_col_teeth'), dataIndex: 'teeth', key: 'teeth', width: 120 },
        { title: this.$t('certCard_print_col_printedAt'), dataIndex: 'printedAt', key: 'printedAt', width: 150 },
        { title: this.$t('certCard_print_col_action'), key: 'printAction', width: 90 },
      ];
    },
  },

  watch: {
    open(val) {
      // 열릴 때만 cards prop을 로컬 사본으로 스냅샷 — 이후 printed 이벤트는 로컬 사본만 갱신
      if (val) {
        this.items = this.cards.map((c) => ({ ...c, printedAt: null }));
      }
    },
  },

  methods: {
    openPrintModal(record) {
      this.printModal.card = { randomNo: record.randomNo, gtin: record.gtin };
      this.printModal.open = true;
    },

    onCardPrinted({ randomNo, printedAt }) {
      const row = this.items.find((r) => r.randomNo === randomNo);
      if (row) row.printedAt = printedAt;
    },

    formatDate(val) {
      if (!val) return '-';
      return new Date(val).toLocaleString();
    },
  },
};
</script>

<!-- 모달은 document.body로 teleport되므로 scoped 스타일이 닿지 않는다(house precedent: IamConsole.vue).
     wrap-class-name으로 부여한 .cert-card-wo-print-overlay로 스코프를 좁힌다. 인쇄 파이프라인과
     무관한 화면 셸(on-screen shell) 클램프만 — @media print 규칙 없음, 인쇄 산출물 불변. -->
<style>
@media (max-width: 767px) {
  .ant-modal-wrap.cert-card-wo-print-overlay {
    z-index: 1100;
  }
  .ant-modal-wrap.cert-card-wo-print-overlay ~ .ant-modal-mask {
    z-index: 1100;
  }
  .cert-card-wo-print-overlay .ant-modal {
    max-width: calc(100vw - 24px);
    margin: 8px auto;
  }
  .cert-card-wo-print-overlay .ant-modal-body {
    max-height: 70vh;
    max-height: 70dvh;
    overflow-y: auto;
  }
}
</style>
